import { useMemo, useState, type ChangeEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  DEFAULT_TAX_BPS,
  TAX_RATES,
  UNIT_TYPES,
  formatPct,
  formatQty,
  marginPct,
  markupPct,
  normalizeCode,
  parseAttributeValues,
  parseQty,
  priceFromMarkup,
  roundPrice,
  uuidv7,
  variantCombinations,
  variantLabel,
  type UnitType,
} from '@pos/core';
import { applyLocalChanges } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { productImageUrl, uploadProductImage } from '../../lib/images.ts';
import { useOnline } from '../../lib/use-online.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Button, Card, Field, Input, PageHeader, Select, Spinner, Toggle, cx, errorMessage } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { MoneyInput } from '../../ui/MoneyInput.tsx';
import { buildProductChanges, initialStockChange, validateProductDraft, type ProductDraft, type VariantDraft } from './changes.ts';
import { useCatalog, useMoney, type Catalog, type CatalogItem } from './data.ts';

/** Variante en edición: además del borrador, el texto crudo de los campos numéricos. */
interface VariantForm extends VariantDraft {
  minStockText: string;
  priceInvalid?: boolean;
  costInvalid?: boolean;
}

interface AttributeForm {
  name: string;
  values: string;
}

export function ProductFormPage() {
  const { id } = useParams();
  const catalog = useCatalog();
  if (!catalog) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }
  const item = id ? catalog.byId.get(id) : undefined;
  if (id && !item) {
    return (
      <Card className="mx-auto max-w-lg text-center">
        <p className="font-medium">No encontramos ese producto.</p>
        <Link to="/productos" className="mt-3 inline-block text-sm font-medium text-brand">
          Volver a productos
        </Link>
      </Card>
    );
  }
  return <ProductForm key={id ?? 'nuevo'} catalog={catalog} item={item ?? null} />;
}

const emptyVariant = (price = 0): VariantForm => ({
  id: uuidv7(),
  attributes: {},
  price,
  cost: null,
  barcode: null,
  sku: null,
  min_stock: null,
  active: true,
  minStockText: '',
});

function toForm(item: CatalogItem | null, catalog: Catalog): { draft: Omit<ProductDraft, 'variants'>; variants: VariantForm[]; attributes: AttributeForm[] } {
  if (!item) {
    return {
      draft: {
        id: uuidv7(),
        name: '',
        category_id: null,
        supplier_id: null,
        unit_type: 'unit',
        tax_bps: DEFAULT_TAX_BPS,
        notes: null,
        active: true,
        image_path: null,
        has_variants: false,
        variant_attributes: [],
      },
      variants: [emptyVariant()],
      attributes: [{ name: 'Talle', values: '' }],
    };
  }
  const { product } = item;
  const variants = item.variants.map(
    (v): VariantForm => ({
      id: v.id,
      attributes: v.attributes,
      price: v.price,
      cost: catalog.costs.get(v.id) ?? null,
      barcode: v.barcode,
      sku: v.sku,
      min_stock: v.min_stock,
      active: v.active,
      minStockText: v.min_stock == null ? '' : formatQty(v.min_stock, product.unit_type).replace(/ \S+$/, ''),
    }),
  );
  const attributes = product.variant_attributes.length
    ? product.variant_attributes.map((name) => ({
        name,
        values: [...new Set(item.variants.map((v) => v.attributes[name]).filter(Boolean))].join(', '),
      }))
    : [{ name: 'Talle', values: '' }];
  return {
    draft: {
      id: product.id,
      name: product.name,
      category_id: product.category_id,
      supplier_id: product.supplier_id,
      unit_type: product.unit_type,
      tax_bps: product.tax_bps,
      notes: product.notes,
      active: product.active,
      image_path: product.image_path,
      has_variants: product.has_variants,
      variant_attributes: product.variant_attributes,
    },
    variants: variants.length ? variants : [emptyVariant()],
    attributes,
  };
}

const ROUNDING = [
  { cents: 1, label: 'Sin redondear' },
  { cents: 1000, label: 'a $10' },
  { cents: 5000, label: 'a $50' },
  { cents: 10000, label: 'a $100' },
];

function ProductForm({ catalog, item }: { catalog: Catalog; item: CatalogItem | null }) {
  const { business, writer, can, device } = useSession();
  const navigate = useNavigate();
  const online = useOnline();
  const money = useMoney();
  const showCosts = can('view_costs');
  const initial = useMemo(() => toForm(item, catalog), [item, catalog]);

  const [draft, setDraft] = useState(initial.draft);
  const [variants, setVariants] = useState<VariantForm[]>(initial.variants);
  const [attributes, setAttributes] = useState<AttributeForm[]>(initial.attributes);
  const [markup, setMarkup] = useState('40');
  const [rounding, setRounding] = useState(1000);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [initialStock, setInitialStock] = useState('');

  const set = <K extends keyof typeof draft>(key: K, value: (typeof draft)[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const setVariant = (index: number, patch: Partial<VariantForm>) =>
    setVariants((list) => list.map((v, i) => (i === index ? { ...v, ...patch } : v)));
  const unit = UNIT_TYPES[draft.unit_type];

  // --- Variantes -----------------------------------------------------------

  const generate = () => {
    const parsed = attributes
      .map((a) => ({ name: a.name.trim(), values: parseAttributeValues(a.values) }))
      .filter((a) => a.name && a.values.length);
    const combos = variantCombinations(parsed);
    if (combos.length === 0) return setErrors({ variants: 'Escribí al menos un atributo con sus valores (ej. Talle: S, M, L).' });
    const key = (attrs: Record<string, string>) => JSON.stringify(Object.entries(attrs).sort());
    const byKey = new Map(variants.map((v) => [key(v.attributes), v]));
    // Si venía de producto simple, la primera combinación reusa su variante (conserva su historial).
    const reusable = !draft.has_variants ? variants[0] : undefined;
    const basePrice = variants[0]?.price ?? 0;
    const next = combos.map((attrs, i) => {
      const existing = byKey.get(key(attrs));
      if (existing) return existing;
      if (i === 0 && reusable) return { ...reusable, attributes: attrs };
      return { ...emptyVariant(basePrice), attributes: attrs, cost: variants[0]?.cost ?? null };
    });
    setVariants(next);
    setDraft((d) => ({ ...d, has_variants: true, variant_attributes: parsed.map((a) => a.name) }));
    setErrors({});
  };

  const toSimple = () => {
    setVariants((list) => [{ ...list[0]!, attributes: {} }]);
    setDraft((d) => ({ ...d, has_variants: false, variant_attributes: [] }));
  };

  // --- Foto ----------------------------------------------------------------

  const onPhoto = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setMessage(null);
    try {
      set('image_path', await uploadProductImage(business.id, draft.id, file));
    } catch (err) {
      setMessage(errorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  // --- Guardar -------------------------------------------------------------

  const save = async () => {
    const formErrors: Record<string, string> = {};
    const finalVariants: VariantDraft[] = variants.map((v, i) => {
      if (v.priceInvalid) formErrors[`variants.${i}.price`] = 'Precio inválido.';
      if (v.costInvalid) formErrors[`variants.${i}.cost`] = 'Costo inválido.';
      let minStock: number | null = null;
      if (v.minStockText.trim()) {
        minStock = parseQty(v.minStockText, draft.unit_type);
        if (minStock === null) formErrors[`variants.${i}.min_stock`] = 'Cantidad inválida.';
      }
      const { minStockText: _t, priceInvalid: _p, costInvalid: _c, ...rest } = v;
      return {
        ...rest,
        barcode: v.barcode ? normalizeCode(v.barcode) || null : null,
        sku: v.sku?.trim() || null,
        min_stock: minStock,
      };
    });
    const full: ProductDraft = { ...draft, variants: finalVariants };
    const all = { ...formErrors, ...validateProductDraft(full, catalog.variants) };
    setErrors(all);
    if (Object.keys(all).length) {
      setMessage('Revisá los campos marcados.');
      return;
    }

    const existing = item ? { product: item.product, variants: item.variants, costs: catalog.costs } : null;
    const now = new Date().toISOString();
    const changes = buildProductChanges(full, existing, business.id, now);
    // Stock inicial: solo al crear un producto sin variantes (las variantes se cargan desde Stock).
    if (!item && !draft.has_variants && initialStock.trim()) {
      const qty = parseQty(initialStock, draft.unit_type);
      if (qty === null) {
        setErrors({ ...all, initial_stock: 'Cantidad inválida.' });
        setMessage('Revisá los campos marcados.');
        return;
      }
      changes.push(...initialStockChange(finalVariants[0]!.id, qty, { businessId: business.id, deviceId: device.id, now, newId: uuidv7 }));
    }
    setBusy(true);
    try {
      await applyLocalChanges(db, changes, writer);
      requestSync(0);
      navigate('/productos');
    } catch (err) {
      setMessage(errorMessage(err));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item) return;
    const now = new Date().toISOString();
    setBusy(true);
    try {
      await applyLocalChanges(
        db,
        [
          { table: 'products', kind: 'patch', id: item.product.id, changes: { deleted_at: now, active: false } },
          // Las variantes también se archivan: así sus códigos de barras quedan libres para otro producto.
          ...item.variants.map((v) => ({ table: 'product_variants' as const, kind: 'patch' as const, id: v.id, changes: { deleted_at: now, active: false } })),
        ],
        writer,
      );
      requestSync(0);
      navigate('/productos');
    } catch (err) {
      setMessage(errorMessage(err));
      setBusy(false);
    }
  };

  const simple = variants[0]!;
  const image = productImageUrl(draft.image_path);

  return (
    <div className="mx-auto max-w-3xl space-y-6 pb-8">
      <PageHeader
        title={item ? 'Editar producto' : 'Nuevo producto'}
        actions={
          <Link to="/productos">
            <Button variant="secondary">Volver</Button>
          </Link>
        }
      />

      <Card className="space-y-4">
        <Field label="Nombre" error={errors.name}>
          {(id) => <Input id={id} value={draft.name} maxLength={120} autoFocus={!item} onChange={(e) => set('name', e.target.value)} />}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Categoría" hint={catalog.categories.length ? undefined : 'Podés crear categorías en Productos → Categorías.'}>
            {(id) => (
              <Select id={id} value={draft.category_id ?? ''} onChange={(e) => set('category_id', e.target.value || null)}>
                <option value="">Sin categoría</option>
                {catalog.categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Proveedor">
            {(id) => (
              <Select id={id} value={draft.supplier_id ?? ''} onChange={(e) => set('supplier_id', e.target.value || null)}>
                <option value="">Sin proveedor</option>
                {catalog.suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Se vende por" hint={draft.unit_type === 'weight' ? 'El precio es por kilo; en la venta se ingresan los gramos.' : undefined}>
            {(id) => (
              <Select id={id} value={draft.unit_type} onChange={(e) => set('unit_type', e.target.value as UnitType)}>
                {Object.entries(UNIT_TYPES).map(([value, def]) => (
                  <option key={value} value={value}>
                    {def.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="IVA">
            {(id) => (
              <Select id={id} value={draft.tax_bps} onChange={(e) => set('tax_bps', Number(e.target.value))}>
                {TAX_RATES.map((t) => (
                  <option key={t.bps} value={t.bps}>
                    {t.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <Toggle checked={draft.active} onChange={(v) => set('active', v)} label="Activo" description="Los productos inactivos no aparecen para vender." />
      </Card>

      {/* Precio, costo y códigos */}
      {!draft.has_variants ? (
        <Card className="space-y-4">
          <h2 className="font-semibold">Precio y códigos</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={`Precio de venta${draft.unit_type !== 'unit' ? ` (por ${unit.symbol})` : ''}`} error={errors['variants.0.price']}>
              {(id) => (
                <MoneyInput
                  id={id}
                  value={simple.price}
                  invalid={!!errors['variants.0.price']}
                  onChange={(c) => setVariant(0, { price: c ?? 0, priceInvalid: c === null })}
                />
              )}
            </Field>
            {showCosts && (
              <Field label="Costo" error={errors['variants.0.cost']} hint="Solo lo ves vos.">
                {(id) => (
                  <MoneyInput
                    id={id}
                    value={simple.cost}
                    invalid={!!errors['variants.0.cost']}
                    onChange={(c, raw) => setVariant(0, { cost: c, costInvalid: c === null && raw.trim() !== '' })}
                  />
                )}
              </Field>
            )}
          </div>
          {showCosts && (
            <div className="rounded-lg bg-slate-50 p-3 text-sm">
              <p className="text-slate-600">
                Recargo sobre el costo: <strong>{formatPct(markupPct(simple.cost, simple.price))}</strong> · Margen sobre la venta:{' '}
                <strong>{formatPct(marginPct(simple.cost, simple.price))}</strong>
              </p>
              {simple.cost !== null && simple.price !== null && simple.cost > simple.price && (
                <p className="mt-1 font-medium text-amber-700">Ojo: el costo es mayor que el precio: cada venta da pérdida.</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-slate-600">Calcular precio con</span>
                <div className="w-20">
                  <Input compact inputMode="decimal" value={markup} onChange={(e) => setMarkup(e.target.value)} aria-label="Recargo en %" />
                </div>
                <span className="text-slate-600">% de recargo, redondeado</span>
                <div className="w-36">
                  <Select compact value={rounding} onChange={(e) => setRounding(Number(e.target.value))} aria-label="Redondeo">
                  {ROUNDING.map((r) => (
                    <option key={r.cents} value={r.cents}>
                      {r.label}
                    </option>
                  ))}
                </Select>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!simple.cost || !Number.isFinite(Number(markup.replace(',', '.')))}
                  onClick={() =>
                    setVariant(0, {
                      price: roundPrice(priceFromMarkup(simple.cost!, Number(markup.replace(',', '.'))), rounding),
                      priceInvalid: false,
                    })
                  }
                >
                  Calcular
                </Button>
              </div>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Código de barras" error={errors['variants.0.barcode']} hint="Podés usar el lector.">
              {(id) => <Input id={id} value={simple.barcode ?? ''} onChange={(e) => setVariant(0, { barcode: e.target.value })} />}
            </Field>
            <Field label="Código interno" error={errors['variants.0.sku']}>
              {(id) => <Input id={id} value={simple.sku ?? ''} onChange={(e) => setVariant(0, { sku: e.target.value })} />}
            </Field>
            <Field label={`Stock mínimo${draft.unit_type !== 'unit' ? ` (${unit.symbol})` : ''}`} error={errors['variants.0.min_stock']} hint="Avisa cuando queda poco.">
              {(id) => (
                <Input id={id} inputMode="decimal" value={simple.minStockText} onChange={(e) => setVariant(0, { minStockText: e.target.value })} />
              )}
            </Field>
          </div>
          {!item && can('adjust_stock') && (
            <Field
              label={`Stock inicial${draft.unit_type !== 'unit' ? ` (${unit.symbol})` : ''} (opcional)`}
              error={errors.initial_stock}
              hint="Lo que hay hoy. Si lo dejás vacío, el producto no lleva control de stock hasta su primera compra o ajuste."
            >
              {(id) => <Input id={id} inputMode="decimal" className="sm:max-w-40" value={initialStock} onChange={(e) => setInitialStock(e.target.value)} />}
            </Field>
          )}
          <button type="button" className="text-sm font-medium text-brand hover:underline" onClick={() => setDraft((d) => ({ ...d, has_variants: true }))}>
            Este producto tiene variantes (talle, color, sabor…)
          </button>
        </Card>
      ) : (
        <Card className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">Variantes</h2>
            <button type="button" className="text-sm font-medium text-brand hover:underline" onClick={toSimple}>
              Volver a producto sin variantes
            </button>
          </div>
          <p className="text-sm text-slate-500">
            Escribí cada atributo y sus valores separados por coma. Después tocá “Generar”: se crean todas las combinaciones.
          </p>
          {attributes.map((a, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-[10rem_1fr_auto]">
              <Input
                placeholder="Atributo (ej. Talle)"
                value={a.name}
                onChange={(e) => setAttributes((list) => list.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                aria-label="Nombre del atributo"
              />
              <Input
                placeholder="Valores (ej. S, M, L, XL)"
                value={a.values}
                onChange={(e) => setAttributes((list) => list.map((x, j) => (j === i ? { ...x, values: e.target.value } : x)))}
                aria-label="Valores del atributo"
              />
              <Button variant="ghost" onClick={() => setAttributes((list) => list.filter((_, j) => j !== i))} disabled={attributes.length === 1}>
                Quitar
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setAttributes((list) => [...list, { name: list.length === 1 ? 'Color' : '', values: '' }])} disabled={attributes.length >= 3}>
              Agregar atributo
            </Button>
            <Button onClick={generate}>Generar variantes</Button>
          </div>
          {errors.variants && <Alert tone="error">{errors.variants}</Alert>}

          {draft.variant_attributes.length > 0 && (
            <div className="space-y-3">
              {variants.map((v, i) => (
                <div key={v.id} className={cx('rounded-lg p-3 ring-1', v.active ? 'ring-slate-200' : 'bg-slate-50 ring-slate-200')}>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="font-medium">{variantLabel(v.attributes) || 'Variante'}</p>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 text-sm text-slate-600">
                        <input type="checkbox" checked={v.active} onChange={(e) => setVariant(i, { active: e.target.checked })} /> Activa
                      </label>
                      <button
                        type="button"
                        className="text-sm text-red-700 hover:underline"
                        // Quitar la última variante deja el producto sin variantes (con su precio, stock e historial).
                        onClick={() => (variants.length === 1 ? toSimple() : setVariants((list) => list.filter((_, j) => j !== i)))}
                        title={variants.length === 1 ? 'Es la única variante: el producto queda sin variantes' : undefined}
                      >
                        Quitar
                      </button>
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-5">
                    <MoneyInput
                      aria-label="Precio"
                      placeholder="Precio"
                      value={v.price}
                      invalid={!!errors[`variants.${i}.price`]}
                      onChange={(c) => setVariant(i, { price: c ?? 0, priceInvalid: c === null })}
                    />
                    {showCosts && (
                      <MoneyInput
                        aria-label="Costo"
                        placeholder="Costo"
                        value={v.cost}
                        invalid={!!errors[`variants.${i}.cost`]}
                        onChange={(c, raw) => setVariant(i, { cost: c, costInvalid: c === null && raw.trim() !== '' })}
                      />
                    )}
                    <Input aria-label="Código de barras" placeholder="Cód. barras" value={v.barcode ?? ''} onChange={(e) => setVariant(i, { barcode: e.target.value })} />
                    <Input aria-label="Código interno" placeholder="Cód. interno" value={v.sku ?? ''} onChange={(e) => setVariant(i, { sku: e.target.value })} />
                    <Input aria-label="Stock mínimo" placeholder="Stock mín." inputMode="decimal" value={v.minStockText} onChange={(e) => setVariant(i, { minStockText: e.target.value })} />
                  </div>
                  {Object.entries(errors)
                    .filter(([k]) => k.startsWith(`variants.${i}.`))
                    .map(([k, msg]) => (
                      <p key={k} className="mt-1 text-sm text-red-600">
                        {msg}
                      </p>
                    ))}
                </div>
              ))}
              <p className="text-sm text-slate-500">
                {variants.length} variante{variants.length === 1 ? '' : 's'} · precios desde {money(Math.min(...variants.map((v) => v.price)))}
              </p>
            </div>
          )}
        </Card>
      )}

      <Card className="space-y-3">
        <h2 className="font-semibold">Foto (opcional)</h2>
        <div className="flex items-center gap-4">
          {image ? (
            <img src={image} alt="" className="size-24 rounded-lg bg-slate-100 object-cover" />
          ) : (
            <span className="flex size-24 items-center justify-center rounded-lg bg-slate-100 text-sm text-slate-400">Sin foto</span>
          )}
          <div className="space-y-2">
            <label className={cx('inline-flex cursor-pointer', (!online || uploading) && 'pointer-events-none opacity-50')}>
              <input type="file" accept="image/*" capture="environment" className="sr-only" onChange={onPhoto} disabled={!online || uploading} />
              <span className="inline-flex h-10 items-center gap-2 rounded-lg bg-white px-4 text-sm font-medium ring-1 ring-slate-300 hover:bg-slate-50">
                {uploading && <Spinner className="size-4" />}
                {image ? 'Cambiar foto' : 'Subir foto'}
              </span>
            </label>
            {image && (
              <button type="button" className="block text-sm text-red-700 hover:underline" onClick={() => set('image_path', null)}>
                Quitar foto
              </button>
            )}
            {!online && <p className="text-sm text-slate-500">Para subir fotos hace falta conexión.</p>}
          </div>
        </div>
      </Card>

      <Card>
        <Field label="Notas (opcional)">
          {(id) => (
            <textarea
              id={id}
              rows={2}
              className="block w-full rounded-lg border-0 px-3 py-2 text-base ring-1 ring-slate-300 focus:ring-2 focus:ring-brand focus:outline-none sm:text-sm"
              value={draft.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
            />
          )}
        </Field>
      </Card>

      {message && <Alert tone="error">{message}</Alert>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {item ? (
          <Button variant="dangerGhost" onClick={() => setConfirmDelete(true)}>
            Eliminar producto
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Link to="/productos">
            <Button variant="secondary">Cancelar</Button>
          </Link>
          <Button size="lg" onClick={save} loading={busy}>
            Guardar
          </Button>
        </div>
      </div>

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="¿Eliminar este producto?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={remove} loading={busy}>
              Eliminar
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-600">
          Deja de aparecer en el catálogo y en la venta. Las ventas y movimientos anteriores se conservan. Si solo querés que no se
          venda por un tiempo, mejor desactivalo.
        </p>
      </Dialog>
    </div>
  );
}
